import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AppProvider, useApp, useAuth } from './context/AppContext';
import { LanguageProvider } from './context/LanguageContext';
import { AdminProvider } from './context/AdminContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthGate } from './components/AuthGate';

// Lazy load heavy components for code splitting
// Dashboard is the heaviest - load it lazily
const Dashboard = lazy(() => import('./components/Dashboard').then(m => ({ default: m.Dashboard })));

// Internal auth pages are no longer routed (RP-2567 — Resova SSO is the only entry).
// AccountSetup/Onboarding/Profile remain reachable as part of the post-auth flow.
const AccountSetup = lazy(() => import('./components/AccountSetup').then(m => ({ default: m.AccountSetup })));
const Onboarding = lazy(() => import('./components/Onboarding').then(m => ({ default: m.Onboarding })));
const Profile = lazy(() => import('./components/Profile').then(m => ({ default: m.Profile })));

const Landing = lazy(() => import('./components/Landing').then(m => ({ default: m.Landing })));

// 404 page
const NotFound = lazy(() => import('./components/NotFound').then(m => ({ default: m.NotFound })));

// Admin pages - lazy load for platform admins only
const AdminLogin = lazy(() => import('./components/admin').then(m => ({ default: m.AdminLogin })));
const AdminLayout = lazy(() => import('./components/admin').then(m => ({ default: m.AdminLayout })));
const AdminGate = lazy(() => import('./components/admin').then(m => ({ default: m.AdminGate })));
const AdminDashboard = lazy(() => import('./components/admin').then(m => ({ default: m.AdminDashboard })));
const AdminAccounts = lazy(() => import('./components/admin').then(m => ({ default: m.AdminAccounts })));
const AdminAccountDetail = lazy(() => import('./components/admin').then(m => ({ default: m.AdminAccountDetail })));
const AdminUsers = lazy(() => import('./components/admin').then(m => ({ default: m.AdminUsers })));
const AdminTeam = lazy(() => import('./components/admin').then(m => ({ default: m.AdminTeam })));
const AdminScraper = lazy(() => import('./components/admin').then(m => ({ default: m.AdminScraper })));
const AdminUsage = lazy(() => import('./components/admin').then(m => ({ default: m.AdminUsage })));
const AdminAuditLog = lazy(() => import('./components/admin').then(m => ({ default: m.AdminAuditLog })));
const AdminFeedback = lazy(() => import('./components/admin').then(m => ({ default: m.AdminFeedback })));

// Prefetch functions for better UX - called after initial render
const prefetchDashboard = () => {
  import('./components/Dashboard');
};

// Loading fallback for lazy components
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center theme-bg-primary">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#2685CF] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-sm theme-text-secondary">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Landing route — auto-redirects to /dashboard once the auth check completes
 * with a valid session/cookie (RP-2567). The landing page renders immediately
 * to avoid a blank flash; the redirect fires only after AppContext initialization
 * finishes and the user is authenticated.
 */
function LandingWithNavigation() {
  const navigate = useNavigate();
  const { isAuthenticated, isInitializing } = useAuth();

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isInitializing, navigate]);

  // Landing renders during init and for unauthenticated visitors.
  // The previous "Get Started" handler is a no-op now that the internal
  // /signin route has been removed; Landing already exposes a Resova login
  // link of its own for unauthenticated users.
  return <Landing onGetStarted={() => { /* /signin removed; SSO handles this */ }} />;
}

/**
 * Replacement for the legacy /signin and /login routes (RP-2567).
 *
 * The internal sign-in screen is no longer reachable. This component absorbs
 * any lingering navigate('/signin') calls from the rest of the app and routes
 * the user to the right destination based on auth state:
 *   - still initializing → loader
 *   - authenticated      → /dashboard
 *   - needs Resova login → hard-redirect to VITE_RESOVA_LOGIN_URL (when set)
 *   - otherwise          → / (landing)
 */
function SignInRedirect() {
  const { isAuthenticated, isInitializing, requiresResovaLogin } = useAuth();

  if (isInitializing) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const resovaLoginUrl = import.meta.env.VITE_RESOVA_LOGIN_URL;
  if (requiresResovaLogin && resovaLoginUrl) {
    window.location.href = resovaLoginUrl;
    return <PageLoader />;
  }

  return <Navigate to="/" replace />;
}

/**
 * Language wrapper that syncs with user authentication state
 */
function LanguageWrapper({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useApp();

  // Prefetch dashboard when user is authenticated (they'll likely navigate there)
  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(prefetchDashboard, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  return (
    <LanguageProvider
      initialLanguage={user?.language}
      isAuthenticated={isAuthenticated}
    >
      {children}
    </LanguageProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <AdminProvider>
          <LanguageWrapper>
            <Suspense fallback={<PageLoader />}>
              <Routes>
              <Route path="/" element={<LandingWithNavigation />} />
              {/* Legacy auth routes — internal sign-in is removed (RP-2567).
                  These now resolve to dashboard / Resova login / landing
                  depending on auth state, so existing navigate('/signin')
                  call sites elsewhere in the app keep working. */}
              <Route path="/signin" element={<SignInRedirect />} />
              <Route path="/login" element={<SignInRedirect />} />
              <Route path="/signup" element={<SignInRedirect />} />
              <Route
                path="/onboarding"
                element={
                  <AuthGate>
                    <ErrorBoundary>
                      <Onboarding />
                    </ErrorBoundary>
                  </AuthGate>
                }
              />
              <Route path="/setup" element={<AccountSetup />} />
              <Route
                path="/dashboard"
                element={
                  <AuthGate>
                    <ErrorBoundary>
                      <Dashboard />
                    </ErrorBoundary>
                  </AuthGate>
                }
              />
              <Route
                path="/profile"
                element={
                  <AuthGate>
                    <ErrorBoundary>
                      <Profile />
                    </ErrorBoundary>
                  </AuthGate>
                }
              />
              <Route
                path="/settings"
                element={
                  <AuthGate>
                    <ErrorBoundary>
                      <Profile />
                    </ErrorBoundary>
                  </AuthGate>
                }
              />
              {/* Admin login - separate from regular auth */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin routes - protected by AdminGate checking admin_session */}
              <Route
                path="/admin"
                element={
                  <AdminGate>
                    <ErrorBoundary>
                      <AdminLayout />
                    </ErrorBoundary>
                  </AdminGate>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="accounts" element={<AdminAccounts />} />
                <Route path="accounts/:id" element={<AdminAccountDetail />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="team" element={<AdminTeam />} />
                <Route path="scraper" element={<AdminScraper />} />
                <Route path="usage" element={<AdminUsage />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
                <Route path="feedback" element={<AdminFeedback />} />
              </Route>

              {/* 404 catch-all route */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </LanguageWrapper>
        </AdminProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
